import { useMemo } from 'react'
import {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlow,
  ReactFlowProvider,
  type Edge,
  type Node,
  type NodeProps,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'

/**
 * The same shape a trigger/criteria/action rule already has, drawn as a graph instead of a
 * vertical form — the visual language Periskope's builder uses, on top of the exact same
 * engine `AutomationRules.tsx`/`FlowBuilder.tsx` already save through.
 *
 * Deliberately read-only: nodes are laid out from the saved rule/flow, not dragged into
 * existence. A flow's "branch" is real here too, not decorative — it's the same "two steps with
 * complementary conditions sharing one trigger" the engine has always used (see the comment on
 * `StepDraft` in FlowBuilder.tsx), just drawn as two paths leaving one trigger instead of two
 * rows in a list. What this does not add is rewiring by drag-and-drop, or nested branching
 * inside one step — the condition engine is still flat AND/OR, a separate, pre-existing gap
 * (`HANDOVER.md`) this canvas does not attempt to close.
 */
export interface CanvasSegment {
  /** "if ticket status is open", or null when the step has no conditions ("always"). */
  criteriaLabel: string | null
  /** "Wait 30m", when the step waits before checking. */
  delayLabel?: string
  disabled?: boolean
  actions: { key: string; label: string }[]
}

/**
 * One path leaving the trigger. A single automation rule is one chain of one segment; a flow's
 * several steps are one chain of several segments stacked in the order they actually run —
 * sequential steps are not a branch and must not be drawn side by side. Two chains only belong
 * side by side when they are genuinely independent paths from the same trigger (the
 * "complementary conditions" substitute for branching described above) — nothing currently
 * feeds this more than one chain, but the layout is ready for whatever eventually does.
 */
export interface CanvasChain {
  id: string
  segments: CanvasSegment[]
}

interface CadNodeData {
  title: string
  subtitle?: string
  tone?: 'trigger' | 'muted'
  chainId?: string
  [key: string]: unknown
}

const NODE_WIDTH = 220
const NODE_HEIGHT = 56
const GAP_X = 56
const GAP_Y = 36

function CadNode({ data }: NodeProps<Node<CadNodeData>>) {
  const className = [
    'cad-canvas-node',
    data.tone === 'trigger' && 'cad-canvas-node-trigger',
    data.tone === 'muted' && 'cad-canvas-node-muted',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <div className={className} style={{ width: NODE_WIDTH }}>
      <Handle type="target" position={Position.Top} style={HANDLE_STYLE} />
      <span style={{ fontWeight: 600 }}>{data.title}</span>
      {data.subtitle && (
        <span className="cad-muted" style={{ fontSize: 'var(--cad-text-sm)' }}>
          {data.subtitle}
        </span>
      )}
      <Handle type="source" position={Position.Bottom} style={HANDLE_STYLE} />
    </div>
  )
}

const HANDLE_STYLE = { opacity: 0, width: 1, height: 1 }
const NODE_TYPES = { cad: CadNode }

function buildGraph(triggerLabel: string, chains: CanvasChain[]): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = []
  const edges: Edge[] = []

  const columns = Math.max(1, chains.length)
  const totalWidth = columns * NODE_WIDTH + (columns - 1) * GAP_X
  const triggerX = totalWidth / 2 - NODE_WIDTH / 2

  nodes.push({
    id: 'trigger',
    type: 'cad',
    position: { x: triggerX, y: 0 },
    data: { title: triggerLabel, tone: 'trigger' },
    draggable: false,
    selectable: false,
  })

  chains.forEach((chain, chainIndex) => {
    const x = chainIndex * (NODE_WIDTH + GAP_X)
    let previousId = 'trigger'
    let row = 1

    chain.segments.forEach((segment, segmentIndex) => {
      const criteriaId = `${chain.id}::seg${segmentIndex}::criteria`
      nodes.push({
        id: criteriaId,
        type: 'cad',
        position: { x, y: row * (NODE_HEIGHT + GAP_Y) },
        data: {
          title: segment.delayLabel ? `${segment.delayLabel}, then check` : 'Check',
          subtitle: segment.criteriaLabel ?? 'always',
          tone: segment.disabled ? 'muted' : undefined,
          chainId: chain.id,
        },
        draggable: false,
      })
      edges.push(edge(previousId, criteriaId, segment.disabled))
      previousId = criteriaId
      row += 1

      segment.actions.forEach((action) => {
        const actionId = `${chain.id}::seg${segmentIndex}::action::${action.key}`
        nodes.push({
          id: actionId,
          type: 'cad',
          position: { x, y: row * (NODE_HEIGHT + GAP_Y) },
          data: { title: action.label, tone: segment.disabled ? 'muted' : undefined, chainId: chain.id },
          draggable: false,
        })
        edges.push(edge(previousId, actionId, segment.disabled))
        previousId = actionId
        row += 1
      })
    })
  })

  return { nodes, edges }
}

function edge(source: string, target: string, muted?: boolean): Edge {
  return {
    id: `${source}->${target}`,
    source,
    target,
    type: 'smoothstep',
    style: { stroke: muted ? 'var(--cad-line)' : 'var(--cad-accent)', strokeWidth: 1.5, opacity: muted ? 0.5 : 1 },
  }
}

/** A height tall enough for the longest chain, so the canvas doesn't clip or `fitView` down to
 *  illegibly small nodes. */
export function estimateCanvasHeight(chains: CanvasChain[]): number {
  const rows = Math.max(
    1,
    ...chains.map((chain) => 1 + chain.segments.reduce((sum, segment) => sum + 1 + segment.actions.length, 0)),
  )
  return Math.max(240, 40 + rows * (NODE_HEIGHT + GAP_Y))
}

export function AutomationCanvas({
  triggerLabel,
  chains,
  onNodeClick,
  height = 320,
}: {
  triggerLabel: string
  chains: CanvasChain[]
  /** Clicking a criteria or action node jumps back to editing that step in the form — the
   *  canvas has no editing surface of its own. */
  onNodeClick?: (chainId: string) => void
  height?: number
}) {
  const { nodes, edges } = useMemo(() => buildGraph(triggerLabel, chains), [triggerLabel, chains])

  return (
    <div className="cad-canvas" style={{ height }}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={NODE_TYPES}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={Boolean(onNodeClick)}
          proOptions={{ hideAttribution: true }}
          onNodeClick={
            onNodeClick
              ? (_, node) => {
                  const { data: nodeData } = node
                  const chainId = (nodeData as CadNodeData).chainId
                  if (chainId) onNodeClick(chainId)
                }
              : undefined
          }
        >
          <Background gap={18} size={1} color="var(--cad-line)" />
          <Controls showInteractive={false} />
        </ReactFlow>
      </ReactFlowProvider>
    </div>
  )
}
